// Paywall 2 pieces (contracts/paywall.md §3, §4): the variant editor (layout, the headline and subheadline in 13
// languages, each its own localized map as the API takes them, plans and their order, the preselected plan,
// price-test products from paywall.allowedProducts, the savings badge, the close delay, the exit offer) and a phone
// sketch of each layout with the App Review must-haves on every one.

import type { Dispatch, SetStateAction } from "react";
import { type Locale, LOCALES } from "../../api/messaging";
import { Button, Chip, Field, Segmented, Select, TextInput } from "../../components/ui";
import {
  BASE_PRODUCTS,
  CLOSE_DELAY_MAX,
  APP_PAYWALL,
  COPY_LIMITS,
  LAYOUT_HINT,
  LAYOUT_LABEL,
  LAYOUTS,
  type Plan,
  PLAN_NAME,
  PLANS,
  planOfProduct,
  productFor,
  savings,
  type Variant,
} from "../../lib/paywall";
import { ContentEditor, type ContentState, type FieldSpec, SwitchRow, textState } from "../messaging/components";
import styles from "../pages.module.css";
import p from "./paywall.module.css";

export const COPY_FIELDS = ["headline", "subheadline"] as const;
export type CopyKey = (typeof COPY_FIELDS)[number];
export const COPY_SPECS: readonly FieldSpec<CopyKey>[] = [
  { key: "headline", label: "Headline", limit: COPY_LIMITS.headline, placeholder: "e.g. Unlock everything" },
  { key: "subheadline", label: "Subheadline", limit: COPY_LIMITS.subheadline, rows: 2, placeholder: "e.g. Unlimited levels, Hard Mode, the full archive and no ads." },
];

/** A variant while it's edited: its settings, and its copy as an editable translation draft. */
export interface VariantState {
  config: Omit<Variant, "headline" | "subheadline">;
  text: ContentState<CopyKey>;
}

export function variantState(v: Variant): VariantState {
  const { headline, subheadline, ...config } = v;
  const content = Object.fromEntries(LOCALES.map((l) => [l, { headline: headline?.[l] ?? "", subheadline: subheadline?.[l] ?? "" }]));
  return { config, text: textState(COPY_FIELDS, content) };
}

/** The Variant to send: `headline` and `subheadline` each a localized map of the languages that have it (none: left out). */
export function variantOut(s: VariantState): Variant {
  const map = (k: CopyKey) => Object.fromEntries(LOCALES.map((l) => [l, s.text.draft[l][k].trim()]).filter(([, t]) => t));
  const headline = map("headline");
  const subheadline = map("subheadline");
  return { ...s.config, ...(Object.keys(headline).length ? { headline } : {}), ...(Object.keys(subheadline).length ? { subheadline } : {}) };
}

const money = (n: number | undefined) => (n === undefined ? "—" : `$${n.toFixed(2)}`);
/** The products a plan can use: its base product and the allowed ones named for it. */
const productsFor = (plan: Plan, allowed: readonly string[], current?: string) =>
  [...new Set([BASE_PRODUCTS[plan], ...allowed.filter((id) => planOfProduct(id) === plan), ...(current ? [current] : [])])];

export function VariantEditor({
  value,
  onChange,
  allowed,
  prices,
  locale,
  onLocale,
  exitOfferReady,
  fallback = {},
  testPrefix = "variant",
}: {
  value: VariantState;
  onChange: Dispatch<SetStateAction<VariantState>>;
  allowed: readonly string[];
  prices: Record<string, number>;
  locale: Locale;
  onLocale: (l: Locale) => void;
  /** paywall.exitOffer is enabled (else the switch explains it does nothing yet). */
  exitOfferReady: boolean;
  /** What an unset field means: the default paywall, for an experiment's variant. */
  fallback?: Variant;
  testPrefix?: string;
}) {
  const c = value.config;
  const set = (patch: Partial<VariantState["config"]>) => onChange((s) => ({ ...s, config: { ...s.config, ...patch } }));
  const shown = c.plans ?? fallback.plans ?? [...PLANS];
  const order = [...shown, ...PLANS.filter((x) => !shown.includes(x))];
  const move = (plan: Plan, by: -1 | 1) => {
    const i = shown.indexOf(plan);
    const j = i + by;
    if (i < 0 || j < 0 || j >= shown.length) return;
    const next = [...shown];
    [next[i], next[j]] = [next[j]!, next[i]!];
    set({ plans: next });
  };
  const toggle = (plan: Plan, on: boolean) => {
    const next = on ? [...shown, plan] : shown.filter((x) => x !== plan);
    const preselected = c.defaultPlan ?? fallback.defaultPlan;
    set({ plans: next, ...(preselected && !next.includes(preselected) ? { defaultPlan: next[0] } : {}) });
  };
  const setProduct = (plan: Plan, id: string) => {
    const products = { ...(c.products ?? {}) };
    if (id === BASE_PRODUCTS[plan]) delete products[plan];
    else products[plan] = id;
    set({ products: Object.keys(products).length ? products : undefined });
  };
  const layout = c.layout ?? fallback.layout ?? "classic";

  return (
    <div className={p.editor} data-testid={`${testPrefix}-editor`}>
      <Field label="Layout" hint={LAYOUT_HINT[layout]}>
        <Segmented label="Layout" value={layout} onChange={(v) => set({ layout: v })} options={LAYOUTS.map((x) => ({ value: x, label: LAYOUT_LABEL[x] }))} />
      </Field>

      <div className={p.plans}>
        <span className="monoLabel">Plans · order, preselected, product{c.plans ? "" : " · as the default paywall"}</span>
        <table className={p.planTable}>
          <tbody>
            {order.map((plan) => {
              const on = shown.includes(plan);
              const product = productFor(c, plan);
              return (
                <tr key={plan} data-off={on ? undefined : ""} data-testid={`${testPrefix}-plan-${plan}`}>
                  <td>
                    <label className={p.check}>
                      <input type="checkbox" checked={on} onChange={(e) => toggle(plan, e.target.checked)} aria-label={`Show ${PLAN_NAME[plan]}`} />
                      {PLAN_NAME[plan]}
                    </label>
                  </td>
                  <td>
                    <span className={p.moves}>
                      <Button size="sm" variant="ghost" disabled={!on || shown.indexOf(plan) === 0} onClick={() => move(plan, -1)} aria-label={`Move ${PLAN_NAME[plan]} up`}>
                        ↑
                      </Button>
                      <Button size="sm" variant="ghost" disabled={!on || shown.indexOf(plan) === shown.length - 1} onClick={() => move(plan, 1)} aria-label={`Move ${PLAN_NAME[plan]} down`}>
                        ↓
                      </Button>
                    </span>
                  </td>
                  <td>
                    <Select value={product} disabled={!on} onChange={(e) => setProduct(plan, e.target.value)} aria-label={`${PLAN_NAME[plan]} product`} data-testid={`${testPrefix}-product-${plan}`}>
                      {productsFor(plan, allowed, product).map((id) => (
                        <option key={id} value={id}>
                          {id}
                          {prices[id] !== undefined ? ` · ${money(prices[id])}` : ""}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td>{product !== BASE_PRODUCTS[plan] ? <Chip tone="violet">price test</Chip> : null}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className={p.inline}>
          <Field label="Preselected">
            <Select value={c.defaultPlan ?? fallback.defaultPlan ?? shown[0] ?? "annual"} onChange={(e) => set({ defaultPlan: e.target.value as Plan })} data-testid={`${testPrefix}-default-plan`}>
              {shown.map((x) => (
                <option key={x} value={x}>
                  {PLAN_NAME[x]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Close button after (s)" hint={`0–${CLOSE_DELAY_MAX}; VoiceOver reaches it at once`}>
            <TextInput
              type="number"
              min={0}
              max={CLOSE_DELAY_MAX}
              step={0.5}
              value={c.closeDelaySec ?? fallback.closeDelaySec ?? 0}
              onChange={(e) => set({ closeDelaySec: e.target.value === "" ? 0 : Number(e.target.value) })}
              data-testid={`${testPrefix}-close-delay`}
            />
          </Field>
        </div>
      </div>

      <SwitchRow
        title="Show the savings badge"
        hint="“Save N%” on the yearly plan, from the shown prices."
        checked={c.showSavings ?? fallback.showSavings ?? true}
        onChange={(v) => set({ showSavings: v })}
        label="Show the savings badge"
        testId={`${testPrefix}-savings`}
      />
      <SwitchRow
        title="Exit offer"
        hint={exitOfferReady ? "When a free player closes an automatic paywall, offer the discounted product once." : "Turned on here, but the exit offer itself is off (Paywall settings → Exit offer): nothing shows yet."}
        checked={c.exitOffer ?? fallback.exitOffer ?? false}
        onChange={(v) => set({ exitOffer: v })}
        label="Exit offer"
        testId={`${testPrefix}-exit-offer`}
      />

      <ContentEditor
        value={value.text}
        onChange={(u) => onChange((s) => ({ ...s, text: typeof u === "function" ? u(s.text) : u }))}
        fields={COPY_SPECS}
        locale={locale}
        onLocale={onLocale}
        note={<p className={styles.faint}>Empty copy uses the default paywall's. A language needs English first: the API takes each text with an English one.</p>}
      />
    </div>
  );
}

// ---------------------------------------------------------------- preview

const COMPARE_ROWS: [string, boolean][] = [
  ["Daily puzzle", true],
  ["Unlimited levels", false],
  ["Hard Mode", false],
  ["Full archive", false],
  ["No ads", false],
];
const PERIOD: Record<Plan, string> = { monthly: "/month", annual: "/year", lifetime: " once" };

/** A phone sketch of the paywall a variant gives, in one language (English, then the default's copy, where empty). */
/** A phone sketch of the paywall a variant gives: unset fields from `fallback` (the default variant), then the app's own paywall. */
export function PaywallPreview({ variant, locale, prices, fallback = {} }: { variant: Variant; locale: Locale; prices: Record<string, number>; fallback?: Variant }) {
  const copy = (k: CopyKey) => variant[k]?.[locale] || variant[k]?.en || fallback[k]?.[locale] || fallback[k]?.en || APP_PAYWALL[k]?.[locale] || APP_PAYWALL[k]?.en || "";
  const plans = variant.plans ?? fallback.plans ?? APP_PAYWALL.plans ?? [...PLANS];
  const preselected = variant.defaultPlan ?? fallback.defaultPlan ?? APP_PAYWALL.defaultPlan;
  const selected = preselected && plans.includes(preselected) ? preselected : plans[0];
  const layout = variant.layout ?? fallback.layout ?? "classic";
  const priceOf = (plan: Plan) => prices[productFor({ products: { ...fallback.products, ...variant.products } }, plan)];
  const save = (variant.showSavings ?? fallback.showSavings ?? true) ? savings(priceOf("monthly"), priceOf("annual")) : null;
  const delay = variant.closeDelaySec ?? fallback.closeDelaySec ?? 0;
  return (
    <div className={p.phone} data-layout={layout} data-testid="paywall-preview">
      <div className={p.top}>
        <span className={p.close} data-delayed={delay > 0 || undefined} title={delay > 0 ? `Fades in after ${delay} s` : "Shown at once"}>
          ×
        </span>
        {delay > 0 ? <span className={p.delay}>{delay} s</span> : null}
      </div>
      <strong className={p.headline}>{copy("headline")}</strong>
      <span className={p.sub}>{copy("subheadline")}</span>
      {layout === "timeline" ? (
        <ol className={p.timeline}>
          <li>
            <b>Today</b>
            <span>Full access to Pro</span>
          </li>
          <li>
            <b>Day 5</b>
            <span>A reminder before the trial ends</span>
          </li>
          <li>
            <b>Day 7</b>
            <span>Billed {money(priceOf("annual"))}, cancel any time before</span>
          </li>
        </ol>
      ) : layout === "compare" ? (
        <table className={p.compare}>
          <thead>
            <tr>
              <th />
              <th>Free</th>
              <th>Pro</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(([row, free]) => (
              <tr key={row}>
                <th scope="row">{row}</th>
                <td>{free ? "✓" : "–"}</td>
                <td>✓</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <ul className={p.planList}>
        {plans.map((plan) => (
          <li key={plan} data-selected={plan === selected || undefined}>
            <span>
              <b>{PLAN_NAME[plan]}</b>
              {plan === "annual" ? <em>1-week free trial</em> : null}
            </span>
            <span className={p.price}>
              {money(priceOf(plan))}
              <small>{PERIOD[plan]}</small>
            </span>
            {plan === "annual" && save ? <i className={p.badge}>Save {save}%</i> : null}
          </li>
        ))}
      </ul>
      <span className={p.cta}>{selected === "annual" ? "Start free trial" : "Continue"}</span>
      <span className={p.legal}>
        {selected === "lifetime" ? "One-time purchase." : `Auto-renews at ${money(priceOf(selected ?? "annual"))}${PERIOD[selected ?? "annual"]} until cancelled.`}
        <br />
        Restore · Terms · Privacy
      </span>
    </div>
  );
}
